import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import clientService from '../../../services/clientService';

const fmt = (n, symbol='฿') => `${symbol}${Number(n || 0).toLocaleString()}`;

const CircleStamp = ({ text = 'PAID', color = '#16a34a', border = '#16a34a' }) => (
  <div style={{ position: 'absolute', left: '50%', top: '55%', transform: 'translate(-50%, -50%) rotate(-12deg)', pointerEvents: 'none', opacity: 0.18 }}>
    <svg width="220" height="220" viewBox="0 0 130 130">
      <circle cx="65" cy="65" r="54" fill="none" stroke={border} strokeWidth="6" strokeDasharray="10 8" />
      <circle cx="65" cy="65" r="44" fill="none" stroke={border} strokeWidth="2" />
      <text x="65" y="72" textAnchor="middle" fontSize="28" fontWeight="900" fill={color} fontFamily="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">{text}</text>
    </svg>
  </div>
);

const PaymentInvoice = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [settings, setSettings] = useState(null);
  const printRef = useRef(null);
  const [docType, setDocType] = useState('INVOICE'); // INVOICE | ESTIMATE | RECEIPT

  useEffect(() => {
    const load = async () => {
      const p = await clientService.getPayment(id);
      setPayment(p);
      try {
        const s = await clientService.getAdminSettings();
        setSettings(s);
      } catch (e) {
        setSettings({ agency: { name: 'Karni Digital Solutions', email: '', phone: '', address: '' }, payments: { currencySymbol: '฿' } });
      }
    };
    load();
    // Pick initial doc type from query
    const t = (searchParams.get('type') || '').toUpperCase();
    if (['INVOICE','ESTIMATE','RECEIPT'].includes(t)) setDocType(t);
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (!payment) return <div className="p-10 text-center">Loading...</div>;

  const symbol = settings?.payments?.currencySymbol || '฿';
  const agency = settings?.agency || { name: 'Karni Digital Solutions', email: '', phone: '', address: '', website: '' };
  const logoUrl = settings?.branding?.logoUrl || '';
  const isPaid = payment.status === 'paid';
  const stampColor = isPaid ? '#16a34a' : (payment.status === 'overdue' ? '#dc2626' : '#f59e0b');
  const stampText = isPaid ? 'PAID' : (payment.status || 'PENDING').toUpperCase();
  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString(); } catch { return d || '-'; }
  };
  const docLabel = docType === 'RECEIPT' ? 'Receipt #' : (docType === 'ESTIMATE' ? 'Estimate #' : 'Invoice #');

  return (
    <div className="bg-gray-100 p-6 print:p-0">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background:#fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body * { visibility: hidden !important; }
          .invoice, .invoice * { visibility: visible !important; }
          .invoice { position: absolute; inset: 0 auto auto 0; width: 190mm !important; margin: 0 !important; box-shadow: none !important; }
          .print\:hidden{ display: none !important; }
        }
      `}</style>
      <div className="invoice mx-auto bg-white shadow-lg rounded-lg relative overflow-hidden" style={{ width: '800px' }} ref={printRef}>
        {/* Watermark/Stamp */}
        <CircleStamp text={stampText} color={stampColor} border={stampColor} />

        {/* Minimal Header (ink-friendly) */}
        <div className="px-8 pt-8">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" />
              ) : (
                <div className="h-16 w-16 bg-primary/10 rounded flex items-center justify-center text-primary font-black">KDS</div>
              )}
              <div className="leading-tight">
                <h2 className="text-2xl font-extrabold text-primary">{agency.name}</h2>
                <div className="text-[11px] text-gray-600">
                  <p>{agency.website || 'https://karnidigitalsolutions.com'}</p>
                  {agency.email && <p>{agency.email}</p>}
                  {agency.phone && <p>{agency.phone}</p>}
                  {agency.address && <p className="max-w-xl whitespace-pre-wrap mt-0.5">{agency.address}</p>}
                </div>
              </div>
            </div>
            <div className="text-right leading-snug">
              <div className="text-xs uppercase tracking-widest text-gray-500">{docType}</div>
              <div className="text-lg font-extrabold text-gray-800">{docLabel} {payment.invoice_number}</div>
              <div className="text-[11px] text-gray-500">Date: {formatDate(payment.payment_date)}</div>
            </div>
          </div>
          <div className="mt-3 border-b border-gray-200"></div>
        </div>

        {/* Body */}
        <div className="p-8">

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-50 rounded border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-1">BILL TO</h3>
            <p className="font-semibold text-gray-900">{payment.client?.company_name || payment.client?.contact_person}</p>
            {payment.client?.email && <p className="text-sm text-gray-600">{payment.client.email}</p>}
            {payment.client?.phone && <p className="text-sm text-gray-600">{payment.client.phone}</p>}
          </div>
          <div className="bg-gray-50 rounded border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-gray-700 mb-1">PROJECT</h3>
            <p className="font-semibold text-gray-900">{payment.project?.project_name || '-'}</p>
            {payment.due_date && <p className="text-sm text-gray-600">Due: {formatDate(payment.due_date)}</p>}
            <p className="text-sm text-gray-600">Method: {payment.payment_method}</p>
            <p className="text-sm text-gray-600">Status: {payment.status}</p>
          </div>
        </div>

        <table className="w-full mb-8">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left px-4 py-2 text-sm text-gray-600">Description</th>
              <th className="text-right px-4 py-2 text-sm text-gray-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="px-4 py-3">
                <div className="font-semibold text-gray-900">{payment.project?.project_name || 'Payment'}</div>
                {payment.notes && <div className="text-sm text-gray-600 whitespace-pre-wrap">{payment.notes}</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold">{fmt(payment.amount, symbol)}</td>
            </tr>
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80 bg-gray-50 border border-gray-100 rounded p-4">
            <div className="flex items-center justify-between text-sm text-gray-700">
              <span>Subtotal</span>
              <span>{fmt(payment.amount, symbol)}</span>
            </div>
            <div className="flex items-center justify-between font-bold text-gray-900 mt-2 border-t pt-2">
              <span>Total</span>
              <span>{fmt(payment.amount, symbol)}</span>
            </div>
          </div>
        </div>

        {/* Terms / Notes */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-gray-700 mb-2">Terms / Notes</h3>
          <p className="text-xs text-gray-600 whitespace-pre-wrap">Payment is due by the due date mentioned above unless otherwise agreed. Thank you for your business.</p>
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          <div>
            <div className="h-12"></div>
            <div className="border-t border-gray-300"></div>
            <p className="text-xs text-gray-600 mt-1">Authorized Signature</p>
          </div>
          <div className="text-right">
            <div className="h-12"></div>
            <div className="border-t border-gray-300"></div>
            <p className="text-xs text-gray-600 mt-1">Received By</p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t pt-4 flex items-center justify-between text-xs text-gray-500">
          <div className="print:hidden flex items-center gap-2 text-gray-600">
            <span>Document:</span>
            {['INVOICE','ESTIMATE','RECEIPT'].map(t => (
              <button key={t} onClick={()=>{ setDocType(t); const q=new URLSearchParams(searchParams); q.set('type', t.toLowerCase()); setSearchParams(q, { replace: true }); }} className={`px-2 py-1 text-xs rounded border ${docType===t? 'bg-primary text-white border-primary' : 'border-gray-300 hover:bg-gray-100'}`}>{t}</button>
            ))}
          </div>
          <button onClick={handlePrint} className="px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded print:hidden">Print</button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInvoice;
